import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { specSheet } from '../../domain/cars/specSheet';

/**
 * The full specification (FR-06).
 *
 * A `<table>`, and deliberately not a stack of divs that looks like one. This is
 * genuinely tabular data — a column of labels against a column of values — and
 * the markup is what lets a screen reader read a row as a pair rather than as
 * two loose strings, and what lets someone navigate down the values without the
 * labels in between.
 *
 * Every row is the same shape, so the whole table is one loop. A row's VALUE
 * arrives from the domain in one of two forms and the difference is not
 * cosmetic: a figure is already a formatted string with an em dash where the
 * catalogue has nothing, and a vocabulary row carries a message KEY because
 * "Twin-turbocharged" is a word that translates. Resolving both here is what
 * keeps `specSheet` free of any knowledge of the language on screen.
 */
export default function CarSpecTable({ car }) {
    const t = useTranslate();
    // Grouping is a fact about a language rather than copy, so the sheet is
    // formatted for the one on screen (see `carProperties.formatSpecNumber`).
    const { languageCode } = useLocale();

    return (
        <table className="spec-table">
            {/* The section heading above already names this table, so the
                caption would be the same words twice on the screen — and taking
                it out of the accessibility tree along with the pixels is what
                `visually-hidden` exists to avoid. */}
            <caption className="visually-hidden">{t('cars.section.specs')}</caption>
            <tbody>
                {specSheet(car, { languageCode }).map((row) => (
                    <tr className="spec-table__row" key={row.key}>
                        {/* `labelValues` is null on every row but the two money
                            ones, where the label carries the year a price was a
                            price in. `t()` with no values is `t()`, so this is
                            one call rather than a branch.

                            The term is wrapped because the screen draws a
                            LEADER — a run of dots from the label out to its
                            value — and the dots are a rule under the cell masked
                            by the term's own ground. Drawing them as characters
                            would put a string of full stops in front of anyone
                            reading this table by ear. */}
                        <th className="spec-table__label" scope="row">
                            <span className="spec-table__term">{t(row.labelKey, row.labelValues)}</span>
                        </th>
                        <td className="spec-table__value">
                            {/* `numeric` is false for exactly one row — the tyre
                                make — and it is the difference between a
                                measurement and a brand name. Tabular figures on
                                "Pirelli P Zero" would set it as if it were one
                                more number in the column. */}
                            {/* KEYED BY THE VALUE ITSELF, which is what makes it
                                animate when — and only when — it actually
                                changes. The table is mounted once and updated in
                                place, so a row whose figure is the same on the
                                next car stays perfectly still; one whose figure
                                is different is a new element, and a new element
                                plays its entrance. */}
                            <span
                                className={row.numeric ? 'figure' : undefined}
                                key={row.valueKey ? t(row.valueKey, row.valueValues) : row.value}
                            >
                                {row.valueKey ? t(row.valueKey, row.valueValues) : row.value}
                            </span>
                            {row.unitKey ? <span className="spec-table__unit">{t(row.unitKey)}</span> : null}
                            {/* The qualifier that places a figure — the rpm a
                                power peak is made at, the speed a standing
                                quarter ended at. It is not part of the value,
                                so it is not in the value's face. */}
                            {row.noteKey ? <span className="spec-table__note">{t(row.noteKey, row.noteValues)}</span> : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
